

import { Component, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MetaUiService } from '../../../core/services/meta-ui.service';
import { MetaUiReadDto } from '../../../core/models/meta-ui.model';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MetaFieldDto } from '../../../core/models/meta-field.model';
import { MetaEntity, MetaService } from '../../../core/services/meta.service';

@Component({
      selector: 'app-meta-ui',
      standalone: true,
      imports: [CommonModule, FormsModule],
      templateUrl: './meta-ui.component.html'
})
export class MetaUiComponent implements OnInit {
      list: MetaUiReadDto[] = [];
      entities: any[] = [];
      totalRecords = 0;
      pageSize = 5;
      pageIndex = 0;
      searchTerm = '';
      loading = false;

      // Form & UI State
      isFormOpen = false;
      selectedId: string | null = null;
      formData: any = { id: '', entityName: '', uiSchema: '' };

      // Getter to easily check if we are editing or creating
      get isEditMode(): boolean {
            return !!this.selectedId;
      }

      private searchSubject = new Subject<string>();
      // constructor(private service: MetaService) { }
      constructor(private metaService: MetaUiService, private service: MetaService) {
            this.searchSubject.pipe(
                  debounceTime(400),
                  distinctUntilChanged()
            ).subscribe(() => {
                  this.pageIndex = 0;
                  this.loadData();
            });
      }

      ngOnInit() {
            this.loadData();
            this.loadEntities(); // Load these for the dropdown
      }

      loadData() {
            this.loading = true;
            this.metaService.getAll(this.pageIndex, this.pageSize, this.searchTerm)
                  .subscribe({
                        next: (res) => {
                              this.list = res.data;
                              this.totalRecords = res.totalRecords;
                              this.loading = false;
                        },
                        error: () => this.loading = false
                  });
      }
      // loadEntities(): void {
      //       // We pass the search term directly to the API
      //       this.service.getAll(this.pageIndex, this.pageSize, this.searchTerm).subscribe({
      //             next: (res: any) => {
      //                   this.entities = res.items;
      //                   this.totalRecords = res.totalCount;
      //             },
      //             error: (err) => console.error('Load Error', err)
      //       });
      // }
      loadEntities(): void {
            this.service.getAllEntities().subscribe({
                  next: (res: any[]) => {
                        // Since the API returns a direct array [...], just assign it directly!
                        // No need for res.items or res.totalCount anymore.
                        this.entities = res;
                  },
                  error: (err) => {
                        console.error('Failed to load entities', err);
                  }
            });
      }

      // loadEntities(): void {
      //       this.service.getAllEntities().subscribe({
      //             next: (res) => {
      //                   this.entities = res; // Direct assignment
      //             }
      //       });
      // }

      // --- Actions ---
      openNewForm() {
            this.selectedId = null;
            this.formData = { id: '', entityName: '', uiSchema: '' };
            this.isFormOpen = true;
      }

      editItem(item: MetaUiReadDto) {
            this.selectedId = item.id;
            // Spread to avoid direct mutation of the list
            this.formData = { ...item };
            this.isFormOpen = true;
      }

      deleteItem(id: string) {
            if (confirm('Are you sure you want to delete this schema?')) {
                  this.loading = true;
                  this.metaService.delete(id).subscribe({
                        next: () => this.loadData(),
                        error: () => this.loading = false
                  });
            }
      }
      // saveData() {
      //       if (!this.formData.entityId) {
      //             alert("Please select an Entity");
      //             return;
      //       }

      //       const payload = {
      //             id: this.formData.id || crypto.randomUUID(),
      //             entityId: this.formData.entityId, // Send the UUID of the selected entity
      //             uiSchema: typeof this.formData.uiSchema === 'string'
      //                   ? this.formData.uiSchema
      //                   : JSON.stringify(this.formData.uiSchema)
      //       };

      //       this.metaService.create(payload).subscribe({
      //             next: () => {
      //                   this.isFormOpen = false;
      //                   this.loadData();
      //             },
      //             error: (err) => console.error(err)
      //       });
      // }
      saveData() {
            // 1. Validation: Ensure an Entity is selected to maintain referential integrity
            if (!this.formData.entityId) {
                  alert("Please select an Entity");
                  return;
            }

            // 2. Preparation: Handle JSONB serialization for PostgreSQL compatibility
            // Your SWERP system uses JSONB for ui_schema
            const payload = {
                  id: this.formData.id || crypto.randomUUID(), // Generate UUID for new records
                  entityId: this.formData.entityId,
                  uiSchema: typeof this.formData.uiSchema === 'string'
                        ? this.formData.uiSchema
                        : JSON.stringify(this.formData.uiSchema)
            };

            // 3. Execution: Determine if this is an Update or a Create
            if (this.formData.id) {
                  // UPDATE: Call the PUT endpoint with the existing ID
                  this.metaService.update(this.formData.id, payload).subscribe({
                        next: () => this.handleSuccess(),
                        error: (err) => console.error('Update Error:', err)
                  });
            } else {
                  // CREATE: Call the POST endpoint for a new record
                  this.metaService.create(payload).subscribe({
                        next: () => this.handleSuccess(),
                        error: (err) => console.error('Create Error:', err)
                  });
            }
      }

      // 4. Cleanup: Refresh the list and close the UI designer
      private handleSuccess() {
            this.isFormOpen = false;
            this.loadData(); // Reloads the paginated list
      }

      closeForm() {
            this.isFormOpen = false;
            this.selectedId = null;
      }

      // --- Table Logic ---
      applyFilter() {
            // This handles the search input specifically with the 400ms debounce
            this.searchSubject.next(this.searchTerm);
      }

      nextPage() {
            if ((this.pageIndex + 1) * this.pageSize < this.totalRecords) {
                  this.pageIndex++;
                  this.loadData();
            }
      }

      prevPage() {
            if (this.pageIndex > 0) {
                  this.pageIndex--;
                  this.loadData();
            }
      }

      get totalPages() {
            return Math.ceil(this.totalRecords / this.pageSize) || 1;
      }
      onPageSizeChange() {
            this.pageIndex = 0; // Always reset to first page
            this.loadData();    // Call directly instead of applyFilter() to avoid debounce delay
      }
}